import z from "zod";

import {
  Button,
  DestructiveButton,
  FieldText,
  Form,
  FormikForm,
  FormRowField,
  ModalDialog,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

type FormValues = {
  name: string;
};

const initialValues: FormValues = { name: "" };

const schema = z.object({
  name: z.string().nonempty().max(100),
});

export interface NewOrgDialogProps {
  create: (name: string) => Promise<void>;
  close: () => void;
}

export const NewOrgDialog = ({ create, close }: NewOrgDialogProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const handleSubmit = async (values: FormValues) => {
    await create(values.name);
    close();
  };

  return (
    <FormikForm
      formatStrings={formatStrings}
      initialValues={initialValues}
      validationSchema={schema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting, submitForm }) => (
        <ModalDialog
          open
          maxWidth="xs"
          header={formatMessage("header")}
          buttons={
            <>
              <Button color="inherit" onClick={close}>
                {formatMessage("cancelLabel")}
              </Button>
              <DestructiveButton disabled={!isValid || isSubmitting} onClick={submitForm}>
                {formatMessage("confirmLabel")}
              </DestructiveButton>
            </>
          }
          onClose={close}
        >
          <p>{formatMessage("description")}</p>
          <Form blurOnEnter={false}>
            <FormRowField name="name">
              <FieldText autoFocus />
            </FormRowField>
          </Form>
        </ModalDialog>
      )}
    </FormikForm>
  );
};

const formatStrings = {
  header: { en: "Add New Workspace" },
  description: { en: "Workspaces provide a shared environment where members of an organization can collaborate." },
  nameLabel: { en: "Workspace Name" },
  confirmLabel: { en: "Add Workspace" },
  cancelLabel: { en: "Cancel" },
};
